import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useProviderCatalog } from '../../../queries';
import {
  ProviderModelSelect,
  defaultProviderAndModel,
} from '../../../components/ProviderModelSelect';
import type { ImageLabOperationType } from '../../../domain';
import { OPERATION_CATALOG } from '../operationCatalog';

interface Props {
  /** The model string the operation panel actually sends to the BE. Empty
   *  string means "use BE default for this op kind". */
  model: string;
  onModelChange: (model: string) => void;
  disabled?: boolean;
  /** Hint shown above the selector — e.g. `"Image model"` or `"LLM"`. */
  label?: string;
  /** `"image"` for op panels (default), `"llm"` for agent surfaces. */
  kind?: 'image' | 'llm';
  /** Preferred provider key to default to when the catalog loads (e.g. `"fal.ai"`
   *  for image, `"gemini"` for LLM). */
  preferredProvider?: string;
  /**
   * The op being configured. When set, the picker filters the model list to
   * only models compatible with that op (per the 2026-05-26 model-vs-op
   * enforcement work — see qa/bugs/2026-05-26-transform-routes-to-text-to-
   * image-when-model-non-edit.md). Ops with `requiresSourceImage=true` only
   * see image-aware models (tags include i2i / edit / inpaint / upscale /
   * face / tryon); TextToImage sees the full list.
   */
  opKind?: ImageLabOperationType;
}

/**
 * Per-panel provider+model picker. Owns provider as local state because the
 * existing operation request DTOs only carry `model` — the BE routes via
 * `IProviderFactory.GetImageProviderForModel(model)`. Provider stays in the
 * UI only to constrain the model list.
 *
 * Defaults: on the first catalog load, seed provider+model from
 * `defaultProviderAndModel(catalog, preferredProvider)` — `fal.ai` for image,
 * `gemini` for llm — and emit the seeded model upward via `onModelChange`.
 */
export const ProviderModelPicker: React.FC<Props> = ({
  model,
  onModelChange,
  disabled,
  label = 'Model',
  kind = 'image',
  preferredProvider = kind === 'image' ? 'fal.ai' : 'gemini',
  opKind,
}) => {
  const catalog = useProviderCatalog();
  const [provider, setProvider] = useState('');
  // Derive "this op consumes a source image" from the local op catalog. Only
  // applies to image-kind pickers — agent / LLM surfaces aren't gated.
  const requiresImageAware =
    kind === 'image' && opKind ? Boolean(OPERATION_CATALOG[opKind]?.requiresSourceImage) : false;
  // From-scratch generate (image op with no source) → only show text-to-image
  // models, so an edit/inpaint model can't be picked and 422 on missing image_urls.
  const textToImageOnly = kind === 'image' && !!opKind && !requiresImageAware;

  useEffect(() => {
    if (provider || !catalog.data) return;
    const list = kind === 'image' ? catalog.data.image : catalog.data.llm;
    const seed = defaultProviderAndModel(list, preferredProvider);
    setProvider(seed.provider);
    if (!model && seed.model) onModelChange(seed.model);
  }, [catalog.data, provider, kind, preferredProvider, model, onModelChange]);

  const handleProviderChange = (next: string) => {
    setProvider(next);
    const list = kind === 'image' ? catalog.data?.image : catalog.data?.llm;
    const entry = list?.find(e => e.key === next);
    const first = entry?.models[0]?.id ?? '';
    if (first) onModelChange(first);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <ProviderModelSelect
        kind={kind}
        provider={provider}
        model={model}
        onProviderChange={handleProviderChange}
        onModelChange={onModelChange}
        variant="stacked"
        size="small"
        disabled={disabled}
        requiresImageAware={requiresImageAware}
        textToImageOnly={textToImageOnly}
      />
    </Box>
  );
};
